import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Pagination, Spinner, Table } from 'react-bootstrap';
import moment from 'moment';
import MetaStore from 'src/store/MetaStore';
import { PlatformCRMWeb } from 'src/services/GrpcService';

interface IProps {
  latitude: number;
  longitude: number;
  onHide: () => void;
  onSelect?: (startAt: string) => void;
}

type SlotsByDate = { [date: string]: PlatformCRMWeb.IAvailableSlotsDay };

const DAY_PARTS = {
  [PlatformCRMWeb.MetaAvailableSlot.DayPart.DAY_PART_MORNING]: 'Утро',
  [PlatformCRMWeb.MetaAvailableSlot.DayPart.DAY_PART_EVENING]: 'Вечер',
};

const formatTime = (value?: string | null) => value ? moment.parseZone(value).format('HH:mm') : '—';

const AvailableSlotsModal = ({ latitude, longitude, onHide, onSelect }: IProps) => {
  const [slotsByDate, setSlotsByDate] = useState<SlotsByDate>({});
  const [selectedDate, setSelectedDate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [showClosedSlots, setShowClosedSlots] = useState(false);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setHasError(false);
    setSlotsByDate({});
    setSelectedDate('');

    MetaStore.findAvailableSlotsByCoordinates({ latitude, longitude, isOpened: !showClosedSlots })
      .then((result) => {
        if (isActive) {
          const days = result.slotsByDate || {};
          setSlotsByDate(days);
          setSelectedDate(Object.keys(days).sort()[0] || '');
        }
      })
      .catch(() => {
        if (isActive) {
          setHasError(true);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [latitude, longitude, attempt, showClosedSlots]);

  const dates = Object.keys(slotsByDate).sort();
  const slots = slotsByDate[selectedDate]?.slots || [];

  return (
    <Modal show size="lg" scrollable onHide={onHide} aria-labelledby="available-slots-title">
      <Modal.Header closeButton>
        <Modal.Title id="available-slots-title" as="h5">Доступные слоты</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Check
          id="available-slots-show-closed"
          type="checkbox"
          custom
          className="mb-3"
          label="Показывать закрытые слоты"
          checked={showClosedSlots}
          onChange={(event) => setShowClosedSlots(event.currentTarget.checked)}
        />
        {isLoading ? (
          <div className="py-4 text-center" role="status">
            <Spinner animation="border" size="sm" className="mr-2" />
            Загрузка слотов…
          </div>
        ) : hasError ? (
          <Alert variant="danger">
            <p>Не удалось загрузить слоты.</p>
            <Button type="button" variant="outline-danger" size="sm" onClick={() => setAttempt(attempt + 1)}>
              Повторить
            </Button>
          </Alert>
        ) : dates.length === 0 ? (
          <Alert variant="danger" className="mb-0">Слотов на адрес не найдено.</Alert>
        ) : (
          <>
            <Pagination className="flex-wrap" aria-label="Даты слотов">
              {dates.map((date) => (
                <Pagination.Item
                  key={date}
                  active={date === selectedDate}
                  activeLabel="Текущая дата"
                  aria-label={moment(date).format('DD.MM.YYYY')}
                  onClick={() => setSelectedDate(date)}
                >
                  {moment(date).format('DD.MM')}
                </Pagination.Item>
              ))}
            </Pagination>
            <Table bordered hover responsive size="sm" className="mb-0">
              <caption className="pt-2">Время региона слота</caption>
              <thead>
                <tr>
                  <th scope="col">День</th>
                  <th scope="col">Интервал</th>
                  <th scope="col">Доступность</th>
                  <th scope="col">Главные / Линейные</th>
                  <th scope="col">План / Факт</th>
                </tr>
              </thead>
              <tbody>
                {slots.map((slot, index) => {
                  const canSelect = Boolean(onSelect && slot.startAt && moment.parseZone(slot.startAt).isValid());
                  const interval = `${formatTime(slot.startAt)}–${formatTime(slot.endAt)}`;

                  return (
                    <tr
                      key={`${selectedDate}-${index}`}
                      className={canSelect ? 'cursor-pointer' : undefined}
                      onClick={canSelect ? () => onSelect!(slot.startAt!) : undefined}
                    >
                      <td className="text-nowrap">{moment(selectedDate).format('DD.MM.YYYY')}</td>
                      <td>
                        {canSelect ? (
                          <Button
                            type="button"
                            variant="link"
                            className="p-0 text-nowrap"
                            aria-label={`Выбрать слот ${moment(selectedDate).format('DD.MM.YYYY')} ${interval}`}
                          >
                            {interval}
                          </Button>
                        ) : <div className="text-nowrap">{interval}</div>}
                        <div>
                          <small className="text-muted">{DAY_PARTS[slot.dayPart || 0]}</small>
                        </div>
                      </td>
                      <td><Badge variant={slot.isAvailable ? 'success' : 'secondary'}>{slot.isAvailable ? 'Открыт' : 'Закрыт'}</Badge></td>
                      <td>{slot.capacity?.general ?? 0} / {slot.capacity?.linear ?? 0}</td>
                      <td>{slot.plan ?? 0} / {slot.fact ?? 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onHide}>Закрыть</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AvailableSlotsModal;
